// Admin auth flow — 2-step (password + TOTP).
//
//   1. POST /admin/login {email,password}
//        - 200 with body          → already had session, mint cookie now
//        - 428 with needs_mfa=true → branch to step 2 (RFC 6585
//          Precondition Required; Go's http.StatusPreconditionRequired)
//   2. POST /admin/login/mfa {challenge_id, factor_id, code}
//        - 200 → cookie minted, csrf_token returned
import { api, setCSRF } from "./api";
import type {
  AdminLoginResponse,
  AdminLoginMFAChallenge,
  AdminMe,
} from "./types";

export interface LoginInput {
  email: string;
  password: string;
}

export type LoginResult =
  | { kind: "success"; data: AdminLoginResponse }
  | { kind: "needs_mfa"; challenge: AdminLoginMFAChallenge };

export async function login(input: LoginInput): Promise<LoginResult> {
  const res = (await api<AdminLoginResponse | AdminLoginMFAChallenge>("/admin/login", {
    method: "POST",
    body: input,
    skipCSRF: true,
    expectMFAChallenge: true,
  })) as AdminLoginResponse | AdminLoginMFAChallenge;
  if ((res as AdminLoginMFAChallenge).needs_mfa) {
    return { kind: "needs_mfa", challenge: res as AdminLoginMFAChallenge };
  }
  const ok = res as AdminLoginResponse;
  setCSRF(ok.csrf_token ?? null);
  return { kind: "success", data: ok };
}

export interface CompleteMFAInput {
  challenge_id: string;
  factor_id: string;
  code: string;
}

export async function completeMFA(input: CompleteMFAInput): Promise<AdminLoginResponse> {
  const res = await api<AdminLoginResponse>("/admin/login/mfa", {
    method: "POST",
    body: input,
    skipCSRF: true,
  });
  setCSRF(res.csrf_token ?? null);
  return res;
}

export async function logout(): Promise<void> {
  try {
    await api("/admin/logout", { method: "POST" });
  } finally {
    setCSRF(null);
  }
}

export async function fetchMeOrNull(): Promise<AdminMe | null> {
  try {
    return await api<AdminMe>("/admin/me");
  } catch (err) {
    if ((err as { status?: number }).status === 401) return null;
    throw err;
  }
}
